import { useState, useEffect, useRef } from 'preact/hooks'
import { contactsApi, type ContactPositionViewDto } from '@/api/contacts'
import type { CompanyLookupViewDto } from '@/api/companies'
import type { UnmatchedPresenter, WebSpeakerPatchItemDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import { useLookups } from '@/hooks/useLookups'
import type { OptionDto } from '@/types/lookups'
import { X, Search, AlertTriangle } from 'lucide-preact'
import { isValidEmail } from '@/utils/validation'
import { isLinkedInProfileUrl, normalizeLinkedInProfileUrl } from '@/utils/linkedInProfileUrl'
import { useAIForm } from '@/hooks'
import type { AIFieldConfig } from '@/hooks/useAIForm'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { TextBox, TextBoxField, Select } from '@/components'
import type { SelectOption } from '@/components/common/Select'
import { SpeakerHeadshotField } from './SpeakerHeadshotField'
import { fieldLimits } from '@/types/field-limits.generated'

const BIO_MAX = fieldLimits.OnlineEventSpeaker.SpeakerBio.MaxLength

type SpeakerAddForm = {
    linkedInUrl: string
    speakerType: string
    companyId: string
    companyName: string
    title: string
    email: string
    bio: string
}

interface Props {
    open: boolean
    onClose: () => void
    onAdd: (speaker: WebSpeakerPatchItemDto) => Promise<void> | void
    workspaceId?: string
    webId?: string
    campaignId?: string
    /** A BigMarker presenter the import couldn't add (ORB-911): its details fill the form, so only the LinkedIn URL is left to find. */
    prefill?: UnmatchedPresenter | null
}

/** Abort a LinkedIn lookup that runs longer than this, so the panel can never hang indefinitely. */
const LOOKUP_TIMEOUT_MS = 45000

/** Why a lookup found no profile. Each one still lets the speaker be added by hand (ORB-938). */
type LookupFailure = 'unreadable' | 'unavailable' | 'timeout'

const LOOKUP_FAILURE_MESSAGES: Record<LookupFailure, string> = {
    unreadable: "LinkedIn didn't return this profile. This usually means it's only visible to signed-in LinkedIn members.",
    unavailable: "The LinkedIn lookup isn't responding right now. Try again in a minute.",
    timeout: 'The LinkedIn lookup took too long to respond.',
}

/**
 * Add-speaker side panel. Step 1 pulls the person from LinkedIn; step 2 lets the
 * operator freely edit the pulled name, company, and headshot before saving.
 */
export function SpeakerAddPanel({ open, onClose, onAdd, workspaceId, webId, campaignId, prefill }: Props) {
    const { showError } = useToast()
    const { getSpeakerTypes } = useLookups()
    const speakerTypeOptions: OptionDto[] = getSpeakerTypes() || []

    const [step, setStep] = useState<1 | 2>(1)
    const [enriching, setEnriching] = useState(false)
    const [companies, setCompanies] = useState<CompanyLookupViewDto[]>([])
    const [positions, setPositions] = useState<ContactPositionViewDto[]>([])
    const [contactId, setContactId] = useState('')
    const [name, setName] = useState('')
    const [profilePicUrl, setProfilePicUrl] = useState('')
    const [customHeadshotUrl, setCustomHeadshotUrl] = useState<string | null>(null)
    const [headshotUploading, setHeadshotUploading] = useState(false)
    const [attemptedSave, setAttemptedSave] = useState(false)
    const [saving, setSaving] = useState(false)
    const [lookupFailure, setLookupFailure] = useState<LookupFailure | null>(null)
    // Set when the speaker is added without a LinkedIn profile; the save sends the URL instead of a ContactId.
    const [manual, setManual] = useState(false)
    const lookupAbortRef = useRef<AbortController | null>(null)

    const aiFields: AIFieldConfig[] = step === 2 ? [
        { aiName: 'speakerBio', formPath: 'bio', displayName: 'Speaker Bio', fieldType: 'textarea', maxLength: BIO_MAX },
        { aiName: 'speakerTitle', formPath: 'title', displayName: 'Speaker Title', fieldType: 'text', maxLength: 200 },
    ] : []

    const { form } = useAIForm<SpeakerAddForm>({
        mode: 'onChange',
        defaultValues: { linkedInUrl: '', speakerType: '', companyId: '', companyName: '', title: '', email: '', bio: '' },
    }, {
        aiContext: {
            campaignId,
            webId,
            location: ChatLocations.WebSpeakerEditor,
            entityType: ChatEntityTypes.Web,
            entityId: webId,
            viewMode: ChatViewModes.Create,
        },
        fields: aiFields,
        loadedData: step === 2 ? { speakerName: name } : undefined,
        contextSuppressed: !open,
    })

    const { register, control, watch, reset, setValue } = form

    const linkedInUrlValue = watch('linkedInUrl')
    const speakerTypeValue = watch('speakerType')
    const companyIdValue = watch('companyId')
    const companyNameValue = watch('companyName')
    const titleValue = watch('title')
    const emailValue = watch('email')
    const bioValue = watch('bio')

    const speakerTypeSelectOptions: SelectOption[] = speakerTypeOptions.map(o => ({
        value: o.Key,
        label: o.Description,
    }))

    // Reset when opened/closed
    useEffect(() => {
        if (!open) return
        lookupAbortRef.current?.abort()
        lookupAbortRef.current = null
        setStep(1)
        reset({
            linkedInUrl: prefill?.LinkedIn ?? '', speakerType: '', companyId: '', companyName: '',
            title: prefill?.Title ?? '', email: prefill?.Email ?? '', bio: (prefill?.Bio ?? '').slice(0, BIO_MAX),
        })
        setEnriching(false)
        setCompanies([])
        setPositions([])
        setContactId('')
        setName('')
        setProfilePicUrl('')
        setCustomHeadshotUrl(prefill?.PhotoUrl || null)
        setAttemptedSave(false)
        setSaving(false)
        setLookupFailure(null)
        setManual(false)
    }, [open, reset, prefill])

    // A failure belongs to the URL that was looked up.
    useEffect(() => { setLookupFailure(null) }, [linkedInUrlValue])

    // Any LinkedIn host is accepted and sent as www.linkedin.com, the only one the lookup takes (ORB-571).
    const linkedInValid = isLinkedInProfileUrl(linkedInUrlValue) && linkedInUrlValue.trim().length <= 2048
    const linkedInInvalidError = !!linkedInUrlValue.trim() && !isLinkedInProfileUrl(linkedInUrlValue)
    const linkedInLengthExceeded = linkedInUrlValue.trim().length > 2048

    const handleLookup = async (): Promise<boolean> => {
        if (!linkedInValid) return false
        const controller = new AbortController()
        lookupAbortRef.current = controller
        let timedOut = false
        const timeoutId = setTimeout(() => { timedOut = true; controller.abort() }, LOOKUP_TIMEOUT_MS)
        setEnriching(true)
        setLookupFailure(null)
        try {
            const res = await contactsApi.lookupByLinkedInUrl(normalizeLinkedInProfileUrl(linkedInUrlValue), { includeCompanies: true, includeSchools: false }, controller.signal)
            if (res.data) {
                const d = res.data
                const fullName = (d.FullName || `${d.FirstName || ''} ${d.LastName || ''}`.trim()).trim()
                setName(fullName || prefill?.Name || 'Unknown')
                setCompanies((d.Companies || []) as CompanyLookupViewDto[])
                setPositions((d.Positions || []) as ContactPositionViewDto[])
                setProfilePicUrl(d.ProfilePicUrl || '')
                setContactId(d.Id || '')
                setManual(false)
                return true
            }
            if (timedOut) {
                setLookupFailure('timeout')
            } else if (res.status === 404) {
                setLookupFailure('unreadable')
            } else if (res.status === 503) {
                setLookupFailure('unavailable')
            } else if (!controller.signal.aborted && res.status !== 422) {
                // A 422 already has the global validation toast with its reason; this one showed its raw JSON.
                showError('Lookup failed', res.error || 'No data returned')
            }
            // Otherwise the lookup was aborted because the panel was closed — stay silent.
            return false
        } catch (e) {
            if ((e as Error).name !== 'AbortError') showError('Lookup error', (e as Error).message)
            return false
        } finally {
            clearTimeout(timeoutId)
            if (lookupAbortRef.current === controller) lookupAbortRef.current = null
            setEnriching(false)
        }
    }

    const handleAddByHand = () => {
        setManual(true)
        setContactId('')
        setName(prefill?.Name ?? '')
        setCompanies([])
        setPositions([])
        setProfilePicUrl('')
        setStep(2)
    }

    const handleClose = () => {
        lookupAbortRef.current?.abort()
        lookupAbortRef.current = null
        setEnriching(false)
        onClose()
    }

    // Close on Escape (matches the app's flyout/panel convention), but never mid-save.
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) handleClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, saving])

    // Selecting a LinkedIn company is an optional quick-fill; the company name below stays freely editable.
    const handleSelectCompany = (cid: string) => {
        setValue('companyId', cid)
        if (!cid) return
        const comp = companies.find(c => c.Id === cid)
        if (comp) setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '')
        const rel = positions.filter(p => p.CompanyId === cid)
        if (rel.length) {
            const latest = rel.sort((a, b) => {
                const ad = a.StartDate ? Date.parse(a.StartDate) : 0
                const bd = b.StartDate ? Date.parse(b.StartDate) : 0
                return bd - ad
            })[0]
            if (latest?.Title) setValue('title', latest.Title.slice(0, 200))
            if (latest?.Description) setValue('bio', latest.Description.slice(0, BIO_MAX))
        }
    }

    const nameLengthExceeded = name.trim().length > 200
    const companyNameLengthExceeded = companyNameValue.trim().length > 200
    const titleLengthExceeded = titleValue.trim().length > 200
    const emailLengthExceeded = emailValue.trim().length > 254
    const bioLengthExceeded = bioValue.trim().length > BIO_MAX
    const emailValid = isValidEmail(emailValue)
    const emailRequiredError = !emailValue.trim() && attemptedSave
    const emailInvalidError = !!emailValue.trim() && !emailValid
    const titleRequiredError = !titleValue.trim() && attemptedSave
    const companyNameRequiredError = !companyNameValue.trim() && attemptedSave
    const nameRequiredError = !name.trim() && attemptedSave
    const speakerTypeInvalidError = !speakerTypeValue.trim() && attemptedSave
    const canNextFromStep1 = linkedInValid && !enriching
    const canSave = name.trim() && !nameLengthExceeded && titleValue.trim() && !titleLengthExceeded && companyNameValue.trim() && !companyNameLengthExceeded && emailValue.trim() && emailValid && !emailLengthExceeded && speakerTypeValue.trim() && !bioLengthExceeded

    const handleSave = async () => {
        setAttemptedSave(true)
        if (!canSave || headshotUploading) return
        setSaving(true)
        try {
            const formValues = form.getValues()
            await onAdd({
                ContactId: manual ? '' : contactId || '',
                LinkedInUrl: manual ? normalizeLinkedInProfileUrl(linkedInUrlValue) : undefined,
                Name: name.trim(),
                Email: formValues.email.trim(),
                Title: formValues.title.trim(),
                CompanyName: formValues.companyName.trim(),
                CompanyId: formValues.companyId || undefined,
                Bio: formValues.bio.trim() || undefined,
                SpeakerType: formValues.speakerType.trim(),
                CustomHeadshotUrl: customHeadshotUrl || undefined,
                // Links the speaker to the listed presenter even when BigMarker had no email or it was edited here.
                UnmatchedPresenterId: prefill?.PresenterId || undefined,
            })
            onClose()
        } catch (e) {
            showError('Add failed', (e as Error).message || 'Unable to add participant')
        } finally {
            setSaving(false)
        }
    }

    const linkedInError = linkedInInvalidError && !linkedInLengthExceeded
        ? 'Enter a LinkedIn profile URL, like https://www.linkedin.com/in/username.'
        : linkedInLengthExceeded
            ? 'LinkedIn URL exceeds maximum length of 2048 characters.'
            : undefined

    const titleError = titleRequiredError
        ? 'Speaker Title is required.'
        : titleLengthExceeded
            ? 'Speaker Title exceeds maximum length of 200 characters.'
            : undefined

    const emailError = emailRequiredError
        ? 'Speaker Email is required.'
        : emailInvalidError
            ? 'Speaker Email is invalid.'
            : emailLengthExceeded
                ? 'Speaker Email exceeds maximum length of 254 characters.'
                : undefined

    const bioError = bioLengthExceeded ? `Speaker Bio exceeds maximum length of ${BIO_MAX} characters.` : undefined
    const speakerTypeError = speakerTypeInvalidError ? 'Invalid SpeakerType.' : undefined
    const companyNameError = companyNameRequiredError
        ? 'Speaker Company Name is required.'
        : companyNameLengthExceeded
            ? 'Speaker Company Name exceeds maximum length of 200 characters.'
            : undefined
    const nameError = nameRequiredError
        ? 'Speaker Name is required.'
        : nameLengthExceeded
            ? 'Speaker Name exceeds maximum length of 200 characters.'
            : undefined

    if (!open) return null

    return (
        <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
            <div class="flex-1 bg-slate-900/40" onClick={handleClose} />
            <div class="w-full sm:w-[480px] lg:w-[540px] ml-auto h-full border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl flex flex-col">
                {/* Header */}
                <div class="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">Add speaker</h3>
                        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {step === 1 ? 'Step 1 of 2 · Find on LinkedIn' : 'Step 2 of 2 · Review and edit'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label="Close panel"
                    >
                        <X class="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div class="flex-1 overflow-y-auto px-6 py-5">
                    {step === 1 && (
                        <div class="space-y-4">
                            <p class="text-sm text-slate-600 dark:text-slate-300">
                                Paste the speaker's LinkedIn profile to pull their name, company, and photo. You can edit all of it on the next step.
                            </p>
                            {prefill && (
                                <p class="text-xs text-slate-500 dark:text-slate-400">
                                    Adding <span class="font-medium text-slate-700 dark:text-slate-200">{prefill.Name}</span> from BigMarker. Their title, email, bio and photo are already filled in.
                                </p>
                            )}
                            <TextBoxField
                                control={control}
                                name="linkedInUrl"
                                labelSize="xs"
                                label="LinkedIn URL"
                                type="url"
                                maxLength={2048}
                                disabled={enriching}
                                placeholder="https://www.linkedin.com/in/username"
                                error={linkedInError}
                            />
                            {enriching && (
                                <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <div class="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                    Fetching speaker details from LinkedIn. This may take up to 30 seconds.
                                </div>
                            )}
                            {lookupFailure && !enriching && (
                                <div role="alert" class="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-100">
                                    <div class="flex items-start gap-2">
                                        <AlertTriangle class="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                                        <div class="space-y-2">
                                            <p>{LOOKUP_FAILURE_MESSAGES[lookupFailure]}</p>
                                            <p>You can still add the speaker by hand.</p>
                                            <button
                                                type="button"
                                                onClick={handleAddByHand}
                                                class="px-3 py-1.5 text-sm rounded-md border border-amber-400 dark:border-amber-600 bg-white dark:bg-slate-800 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-slate-700"
                                            >Add by hand</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div class="space-y-5">
                            {manual && (
                                <p class="text-xs text-slate-500 dark:text-slate-400">
                                    Adding by hand. Nothing was pulled from LinkedIn, so fill in the details and add a photo.
                                </p>
                            )}
                            <SpeakerHeadshotField
                                name={name}
                                linkedInPhotoUrl={profilePicUrl}
                                customUrl={customHeadshotUrl}
                                onChange={setCustomHeadshotUrl}
                                onUploadingChange={setHeadshotUploading}
                                workspaceId={workspaceId}
                                webId={webId}
                            />

                            <TextBox
                                labelSize="xs"
                                label="Name"
                                name="name"
                                value={name}
                                onChange={setName}
                                placeholder="Speaker name"
                                error={nameError}
                            />

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Select
                                    labelSize="xs"
                                    label="Role"
                                    options={speakerTypeSelectOptions}
                                    placeholder="Select role"
                                    error={speakerTypeError}
                                    value={speakerTypeValue}
                                    {...register('speakerType')}
                                />
                                {companies.length > 0 && (
                                    <Select
                                        labelSize="xs"
                                        label="Quick-fill from LinkedIn"
                                        name="companyId"
                                        options={companies.map(c => ({ value: c.Id, label: c.Name || c.UniversalName || c.NormalizedName || 'Company' }))}
                                        placeholder="Optional"
                                        value={companyIdValue}
                                        onChange={(e: Event) => handleSelectCompany((e.currentTarget as HTMLSelectElement).value)}
                                    />
                                )}
                            </div>

                            <TextBoxField
                                control={control}
                                name="companyName"
                                labelSize="xs"
                                label="Company"
                                maxLength={200}
                                placeholder="Company name"
                                error={companyNameError}
                            />

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <TextBoxField
                                    control={control}
                                    name="title"
                                    labelSize="xs"
                                    label="Title"
                                    maxLength={200}
                                    placeholder="Title"
                                    error={titleError}
                                />
                                <TextBoxField
                                    control={control}
                                    name="email"
                                    labelSize="xs"
                                    label="Email"
                                    type="email"
                                    maxLength={254}
                                    placeholder="name@example.com"
                                    error={emailError}
                                />
                            </div>

                            <TextBoxField
                                control={control}
                                name="bio"
                                labelSize="xs"
                                label="Bio (optional)"
                                multiline
                                rows={5}
                                maxLength={BIO_MAX}
                                showCharCounter
                                placeholder="Short bio"
                                error={bioError}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div class="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                    {step === 1 ? (
                        <>
                            <button
                                type="button"
                                onClick={handleClose}
                                class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >Cancel</button>
                            <button
                                type="button"
                                disabled={!canNextFromStep1}
                                onClick={async () => { if (await handleLookup()) setStep(2) }}
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                                {enriching
                                    ? <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                                    : <Search class="w-4 h-4" />}
                                {enriching ? 'Fetching…' : 'Look up'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                class="px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >Back</button>
                            <button
                                type="button"
                                disabled={!canSave || saving || headshotUploading}
                                onClick={handleSave}
                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                                {saving && <div class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />}
                                {saving ? 'Saving…' : 'Add speaker'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
